import type { MapSide, Seam } from "daedalus-core";
import { SOLID } from "../collision/flags.ts";
import { CELLS_PER_TILE, SIXTEENTHS, type CollisionGrid } from "../movement/step.ts";

/**
 * The hand band (ADR-147 decisions 1 to 4): the strip one tile deep, `HAND_BAND_SIXTEENTHS`, of map `b`'s ground along a
 * seam that either map's server may simulate a character on. A character of `a` may step that far into `b`'s ground,
 * checked against `b`'s own collision; a character of `b` may step past its line by the one step that triggers its
 * handback. The two triggers are the hysteresis: `a` hands a character over at the band's full depth, `b` hands it back
 * one step past its line, so between them lies one tile in which ownership does not change. Everything here is pure
 * data over places, seams and collision strips, so the map server, the tests and the page's prediction use one rule.
 */

/**
 * How deep the hand band is, in sixteenths: one tile, which is also how far past the line a character of `a` is when it
 * is handed over.
 */
export const HAND_BAND_SIXTEENTHS = SIXTEENTHS;

/**
 * How many rows of the neighbour's ground a band holds, nearest the line first: two tiles in collision cells, so the
 * step that meets a trigger, and the feet box's overhang past the centre it is measured by, stand on the neighbour's
 * ground and not on a wall.
 */
export const BAND_CELLS = 2 * CELLS_PER_TILE;

/**
 * How many ticks a refused crossing waits before it asks again, and before a refusal's notice is shown again (decision
 * 20): five seconds.
 */
export const CROSSING_RETRY_TICKS = 100;

/**
 * A map as the band reads it: its id, its place on the world grid in whole tiles with its depth, its size in tiles, and
 * whether it is blank, which has no ground. A map with no place stands alone and has no neighbour.
 */
export interface BandMapSize {
  readonly id: string;
  readonly place?: { readonly x: number; readonly y: number; readonly depth?: number } | undefined;
  readonly width: number;
  readonly height: number;
  readonly blank?: boolean | undefined;
}

/**
 * One seam side of a map that a character may be handed across: the side of this map the neighbour lies on, the
 * neighbour's id, whether this map is the seam's `a` (the north or west map, which hands over at the band's depth), which
 * tiles along this map's edge are open (1) and which are walls (0), and how to translate a position of this map into the
 * neighbour's frame. `ownCells` and `neighbourCells` are the lengths of the two edges in collision cells and
 * `cellShift` is added to a cell along this map's edge to give the neighbour's cell along its own. `line16` is the line's
 * coordinate in sixteenths on the axis it crosses.
 */
export interface BandLink {
  readonly side: MapSide;
  readonly neighbour: string;
  readonly ownerIsA: boolean;
  readonly along: Uint8Array;
  readonly ownCells: number;
  readonly neighbourCells: number;
  readonly cellShift: number;
  readonly dx: number;
  readonly dy: number;
  readonly axis: "x" | "y";
  readonly line16: number;
}

/**
 * A map's hand band: its links and the grid its characters step against, which is the map's own grid with the band's
 * ground beyond its edges.
 */
export interface HandBand {
  readonly links: readonly BandLink[];
  readonly grid: CollisionGrid;
}

/**
 * A map's alongside: where a neighbour lies, the stretch of edge they share in this map's tiles and the origins both
 * maps count along that edge from.
 */
interface Alongside {
  readonly side: MapSide;
  readonly from: number;
  readonly to: number;
  readonly ownOrigin: number;
  readonly otherOrigin: number;
}

/**
 * Where an other map touches this one, or null when it does not share a stretch of edge at the same depth.
 *
 * @param self This map, placed.
 * @param other The other map, placed.
 * @returns The side, the stretch and the origins, or null.
 */
function alongside(self: BandMapSize, other: BandMapSize): Alongside | null {
  const s = self.place;
  const o = other.place;
  if (s === undefined || o === undefined || (s.depth ?? 0) !== (o.depth ?? 0)) {
    return null;
  }
  const xStart = Math.max(s.x, o.x);
  const xEnd = Math.min(s.x + self.width, o.x + other.width);
  const yStart = Math.max(s.y, o.y);
  const yEnd = Math.min(s.y + self.height, o.y + other.height);
  if (xEnd > xStart) {
    if (o.y === s.y + self.height) {
      return { side: "south", from: xStart - s.x, to: xEnd - s.x, ownOrigin: s.x, otherOrigin: o.x };
    }
    if (o.y + other.height === s.y) {
      return { side: "north", from: xStart - s.x, to: xEnd - s.x, ownOrigin: s.x, otherOrigin: o.x };
    }
  }
  if (yEnd > yStart) {
    if (o.x === s.x + self.width) {
      return { side: "east", from: yStart - s.y, to: yEnd - s.y, ownOrigin: s.y, otherOrigin: o.y };
    }
    if (o.x + other.width === s.x) {
      return { side: "west", from: yStart - s.y, to: yEnd - s.y, ownOrigin: s.y, otherOrigin: o.y };
    }
  }
  return null;
}

/**
 * Which tiles along this map's edge a seam lets a character cross (decision 3): the whole shared stretch of an `open`
 * seam, the tiles within a crossing's span of a `closed-but` seam, and none of a `closed` one. A span is a pair of
 * inclusive corner numbers of `a`'s edge, as `Seam` defines it (257 corners to an edge of 256 tiles) and the editor's
 * seam checks write it (a walkable run is a run of at least two corners): corners `from` to `to` bound the tiles
 * `from` to `to - 1`. A crossing with no position yet opens nothing.
 *
 * @param seam The seam.
 * @param shared The stretch in this map's tiles, `from` inclusive and `to` exclusive.
 * @param length This map's edge in tiles.
 * @param aOrigin The place of `a` along the edge, in tiles; the number the spans count from.
 * @param ownOrigin The place of this map along the edge.
 * @returns One byte a tile, 1 where a crossing is allowed.
 */
function openTiles(seam: Seam, shared: { readonly from: number; readonly to: number }, length: number, aOrigin: number, ownOrigin: number): Uint8Array {
  const along = new Uint8Array(length);
  if (seam.state === "closed") {
    return along;
  }
  for (let tile = shared.from; tile < shared.to; tile += 1) {
    const cornerTile = ownOrigin + tile - aOrigin;
    along[tile] = seam.state === "open" || seam.crossings.some((c) => c.from !== undefined && c.to !== undefined && cornerTile >= c.from && cornerTile < c.to) ? 1 : 0;
  }
  return along;
}

/**
 * The links of a map to its placed neighbours across seams that are not closed: for each neighbour that shares a stretch
 * of edge at the same depth, is not blank and has a seam with this map that is `open` or `closed-but`, the side, the open
 * tiles and the translation (decisions 1 and 3). A side with a seam that is `closed`, with none, or with a blank
 * neighbour has no link, so the line there is a wall as a map edge is.
 *
 * @param self This map.
 * @param others The world's other maps.
 * @param seams The world's seams.
 * @returns The links, in the order of `others`.
 */
export function bandLinks(self: BandMapSize, others: readonly BandMapSize[], seams: readonly Seam[]): BandLink[] {
  const links: BandLink[] = [];
  for (const other of others) {
    const near = other.blank === true ? null : alongside(self, other);
    const seam = near === null ? undefined : seams.find((s) => (s.a === self.id && s.b === other.id) || (s.a === other.id && s.b === self.id));
    if (near === null || seam === undefined || seam.state === "closed") {
      continue;
    }
    const ownerIsA = seam.a === self.id;
    if (ownerIsA !== (near.side === "south" || near.side === "east")) {
      continue;
    }
    const horizontal = near.side === "south" || near.side === "north";
    const ownLength = horizontal ? self.width : self.height;
    const otherLength = horizontal ? other.width : other.height;
    const aOrigin = ownerIsA ? near.ownOrigin : near.otherOrigin;
    const place = self.place as { readonly x: number; readonly y: number };
    const there = other.place as { readonly x: number; readonly y: number };
    const across = (horizontal ? self.height : self.width) * SIXTEENTHS;
    const otherAcross = (horizontal ? other.height : other.width) * SIXTEENTHS;
    const slide = (horizontal ? place.x - there.x : place.y - there.y) * SIXTEENTHS;
    const toward = near.side === "south" || near.side === "east" ? -across : otherAcross;
    links.push({
      side: near.side,
      neighbour: other.id,
      ownerIsA,
      along: openTiles(seam, near, ownLength, aOrigin, near.ownOrigin),
      ownCells: ownLength * CELLS_PER_TILE,
      neighbourCells: otherLength * CELLS_PER_TILE,
      cellShift: (near.ownOrigin - near.otherOrigin) * CELLS_PER_TILE,
      dx: horizontal ? slide : toward,
      dy: horizontal ? toward : slide,
      axis: horizontal ? "y" : "x",
      line16: near.side === "south" || near.side === "east" ? across : 0,
    });
  }
  return links;
}

/**
 * The neighbour's strip laid in this map's frame: `BAND_CELLS` rows of this map's edge in cells, the row `d` nearest
 * the line first, each cell the neighbour's collision at the same place on the ground, and `SOLID` where the link is shut
 * or the neighbour has no ground.
 *
 * @param link The link.
 * @param strip The neighbour's ground along the edge, `BAND_CELLS` rows of `link.neighbourCells`, the row nearest the
 *   line first.
 * @returns The slab, `BAND_CELLS` rows of `link.ownCells`.
 */
export function bandSlab(link: BandLink, strip: Readonly<Uint8Array>): Uint8Array {
  const slab = new Uint8Array(BAND_CELLS * link.ownCells).fill(SOLID);
  for (let cell = 0; cell < link.ownCells; cell += 1) {
    const across = cell + link.cellShift;
    if (link.along[Math.floor(cell / CELLS_PER_TILE)] !== 1 || across < 0 || across >= link.neighbourCells) {
      continue;
    }
    for (let d = 0; d < BAND_CELLS; d += 1) {
      slab[d * link.ownCells + cell] = strip[d * link.neighbourCells + across] as number;
    }
  }
  return slab;
}

/**
 * A map's hand band over its grid: the grid a character steps against is the map's own, sharing its cells, with the
 * links' slabs, the links of one side laid side by side in one, beyond its south, east, north and west edges and a wall elsewhere, so a closed span is a wall without further code. The corners between two sides are walls.
 *
 * @param base The map's own grid, whose cells the result shares.
 * @param links The map's links.
 * @param strips The neighbours' strips, one for each link in its order.
 * @returns The band.
 * @throws Error when the strips are not one for each link.
 */
export function buildHandBand(base: CollisionGrid, links: readonly BandLink[], strips: readonly Readonly<Uint8Array>[]): HandBand {
  if (strips.length !== links.length) {
    throw new Error(`${links.length} links need ${links.length} strips and got ${strips.length}`);
  }
  const slabs: { north?: Uint8Array; south?: Uint8Array; east?: Uint8Array; west?: Uint8Array } = {};
  links.forEach((link, i) => {
    const slab = bandSlab(link, strips[i] as Uint8Array);
    const side = (slabs[link.side] ??= new Uint8Array(BAND_CELLS * link.ownCells).fill(SOLID));
    for (let cell = 0; cell < link.ownCells; cell += 1) {
      if (link.along[Math.floor(cell / CELLS_PER_TILE)] === 1) {
        for (let d = 0; d < BAND_CELLS; d += 1) {
          side[d * link.ownCells + cell] = slab[d * link.ownCells + cell] as number;
        }
      }
    }
  });
  const { width, height, outside } = base;
  const beyond = (cx: number, cy: number): number => {
    if (cx >= 0 && cx < width) {
      if (cy >= height) {
        const d = cy - height;
        return slabs.south === undefined || d >= BAND_CELLS ? SOLID : (slabs.south[d * width + cx] as number);
      }
      if (cy < 0) {
        const d = -1 - cy;
        return slabs.north === undefined || d >= BAND_CELLS ? SOLID : (slabs.north[d * width + cx] as number);
      }
    } else if (cy >= 0 && cy < height) {
      if (cx >= width) {
        const d = cx - width;
        return slabs.east === undefined || d >= BAND_CELLS ? SOLID : (slabs.east[d * height + cy] as number);
      }
      const d = -1 - cx;
      return slabs.west === undefined || d >= BAND_CELLS ? SOLID : (slabs.west[d * height + cy] as number);
    }
    return outside;
  };
  return { links, grid: { width, height, cells: base.cells, outside, beyond } };
}

/**
 * The link whose trigger a position has met (decision 2): a position of `a` 16 sixteenths past its south or east line,
 * or of `b` past its north or west line, through the neighbour whose open stretch of the edge the position lies along,
 * so several neighbours on one side are told apart. Allocates nothing.
 *
 * @param band The band.
 * @param x Feet x in sixteenths.
 * @param y Feet y in sixteenths.
 * @returns The link, or null when the position is inside either trigger.
 */
export function crossingLink(band: HandBand, x: number, y: number): BandLink | null {
  for (const link of band.links) {
    const at = link.axis === "x" ? x : y;
    if (link.line16 === 0 ? at < 0 : at >= link.line16 + HAND_BAND_SIXTEENTHS) {
      if (link.along[Math.floor((link.axis === "x" ? y : x) / SIXTEENTHS)] === 1) {
        return link;
      }
    }
  }
  return null;
}

/**
 * A position of this map in the neighbour's frame.
 *
 * @param link The link.
 * @param x Feet x in sixteenths of this map.
 * @param y Feet y.
 * @returns The position in the neighbour's frame.
 */
export function translateTo(link: BandLink, x: number, y: number): { readonly x: number; readonly y: number } {
  return { x: x + link.dx, y: y + link.dy };
}

/**
 * A position brought back to the line on this map's side, along the side kept: where a character a crossing holds
 * stands, and where a refused one is returned to.
 *
 * @param link The link it crosses.
 * @param x Feet x in sixteenths.
 * @param y Feet y.
 * @returns The position on the line.
 */
export function holdAtLine(link: BandLink, x: number, y: number): { readonly x: number; readonly y: number } {
  return link.axis === "x" ? { x: link.line16, y } : { x, y: link.line16 };
}
